// ===========================================
// Meet & Greet API Functions
// ===========================================
// API calls for meet & greet booking and management

import type { SpamCheckStatus } from '../components/admin/SpamCheckChip';
import { apiClient } from '../services/api';
import type { AdminConvertRegistrationFee, ForfeitEnquiryOutcome } from './familyPayments.api';
import type { EnquiryStage } from './enquiry.api';

// ===========================================
// TYPES
// ===========================================

export type MeetAndGreetStatus =
  | 'PENDING_VERIFICATION'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'WAITLISTED'
  | 'REJECTED'
  | 'CONVERTED'
  | 'CANCELLED';

export interface MeetAndGreetFormData {
  schoolId: string;
  studentFirstName: string;
  studentLastName: string;
  studentAge: number;
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2Name?: string;
  contact2Email?: string;
  contact2Phone?: string;
  contact2Relationship?: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  instrumentId?: string;
  preferredDateTime?: string;
  additionalNotes?: string;
  /** #412: the Turnstile token, or '' when the widget produced none (the server asks Cloudflare either way). */
  turnstileToken?: string;
  /**
   * The signed `?e=` token from a link the office sent. Sent when present, omitted otherwise; the
   * server books either way and never says whether it linked (the page must not either).
   */
  enquiryToken?: string | null;
}

export interface MeetAndGreet {
  id: string;
  schoolId: string;
  studentFirstName: string;
  studentLastName: string;
  studentAge: number;
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2Name: string | null;
  contact2Email: string | null;
  contact2Phone: string | null;
  contact2Relationship: string | null;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  instrumentId: string | null;
  instrument: { id: string; name: string; color: string | null } | null;
  preferredDateTime: string | null;
  scheduledDateTime: string | null;
  additionalNotes: string | null;
  status: MeetAndGreetStatus;
  /** The enquiry it is linked to (the admin list sends every scalar column); null = booked on its own. */
  enquiryId?: string | null;
  /**
   * The linked enquiry (list and detail only; mutation responses do not carry it, so refetch).
   * Null = not linked, OR the caller lacks `enquiries.read`. The stage label is per school.
   */
  enquiry?: LinkedEnquiry | null;
  assignedTeacherId: string | null;
  assignedTeacher: {
    id: string;
    user: { id: string; firstName: string; lastName: string; email: string };
  } | null;
  followUpNotes: string | null;
  rejectionReason: string | null;
  /** #255: the scheduled time for which the teacher's notes are still awaited; null when none are. */
  notesDueAt?: string | null;
  /** #255: the server's verdict (the meeting has ENDED and notes are awaited); the browser never computes it. */
  notesMissing?: boolean;
  /** #303: set only when notes record that the family attended. */
  completedAt?: string | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;

  // --- Registration / verification link state ---
  // NOTE: the tokens themselves are NEVER sent to the client — the backend
  // strips them and exposes only this boolean plus the timestamps below.
  /** True when a registration link is live right now. */
  hasRegistrationToken?: boolean;
  /** The server's ONE rule for the Resend action (#249): issued a link before,
   *  APPROVED, never converted. Gate on THIS — never on `status` or on
   *  `hasRegistrationToken`: an email correction cancels the live link but the
   *  booking stays resendable, while a legacy admin-created booking (APPROVED, never
   *  issued a link; none since #425) converts manually and is never re-sent one. */
  canResendRegistration?: boolean;
  /** Set once the booking became a family (sent by every admin route; #214 can leave it set on an APPROVED row). */
  convertedFamilyId?: string | null;
  /** #430: this booking's contact 1 or 2 belongs to a family (or login) the school already has.
   *  Null/absent = no match. `hardCollision` = a contact-1 login (Convert refuses it outright). */
  existingFamily?: ExistingFamilyInfo | null;
  registrationTokenExpiresAt?: string | null;
  registrationTokenUsedAt?: string | null;
  registrationTokenSentAt?: string | null;
  registrationTokenResentAt?: string | null;
  verificationTokenSentAt?: string | null;
  verificationTokenResentAt?: string | null;
  /** #412: Turnstile outcome for a PUBLIC booking; null = before #412 or staff-created. */
  spamCheck?: SpamCheckStatus | null;
  /** #374 release B: the 6-month waitlist clock. Null unless WAITLISTED with a clock; the SERVER decides the state. */
  waitlistClock?: WaitlistClock | null;
}

export interface WaitlistClock {
  /** ISO instants. */
  waitlistedAt: string;
  /** The waitlist day is the earliest it can have been (waitlisted before the clock existed): show "on or after". */
  estimated?: boolean;
  reviewAt: string;
  state: 'WAITING' | 'REVIEW_SOON' | 'REVIEW_DUE';
}

/** The enquiry's fate after Confirm waitlist lapsed (same shape as the forfeit outcome). */
export interface WaitlistConfirmResult {
  forfeitedFeeId: string | null;
  enquiry: ForfeitEnquiryOutcome;
}

export interface LinkedEnquiry {
  id: string;
  firstName: string;
  lastName: string;
  stage: EnquiryStage;
}

export interface ExistingFamilyInfo {
  familyId: string | null;
  /** The family's PRIMARY parent: the id the families page is keyed on. */
  primaryParentId: string | null;
  matchedOn: 'LOGIN' | 'PARENT_CONTACT';
  matchedContact: 1 | 2;
  /** A login with no active family (staff, adult student): chip reads "Has a login". */
  loginWithoutFamily: boolean;
  hardCollision: boolean;
}

export interface MeetAndGreetCounts {
  PENDING_VERIFICATION: number;
  PENDING_APPROVAL: number;
  APPROVED: number;
  WAITLISTED: number;
  REJECTED: number;
  CONVERTED: number;
  CANCELLED: number;
}

export interface SchoolInfo {
  id: string;
  name: string;
  isActive: boolean;
  /** #412: the PUBLIC Turnstile site key; null = not configured (render no widget). */
  turnstileSiteKey: string | null;
}

export interface InstrumentOption {
  id: string;
  name: string;
}

export interface MeetAndGreetTimeSlot {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

export interface MeetAndGreetSettingsResponse {
  id: string;
  schoolId: string;
  sessionDuration: number;
  bufferTime: number;
  advanceBookingDays: number;
  minimumNoticeHours: number;
  timeSlots: MeetAndGreetTimeSlot[];
}

export interface MeetAndGreetAvailabilityResponse {
  settings: {
    sessionDuration: number;
    bufferTime: number;
    advanceBookingDays: number;
    minimumNoticeHours: number;
  };
  timeSlots: MeetAndGreetTimeSlot[];
  teachers: Array<{ id: string; name: string }>;
  bookedSlots: Array<{ dateTime: string | null; teacherId: string | null }>;
  /** APPROVED teacher away periods (TeacherUnavailability) in the booking window. */
  unavailablePeriods?: Array<{ teacherId: string; startDate: string; endDate: string }>;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// PUBLIC API (No Auth)
// ===========================================

export const meetAndGreetPublicApi = {
  /**
   * Get school info by slug (for booking form)
   */
  getSchoolInfo: (schoolSlug: string) =>
    apiClient
      .get<ApiResponse<SchoolInfo>>(`/public/schools/${schoolSlug}/info`)
      .then((res) => res.data),

  /**
   * Get available instruments for a school
   */
  getSchoolInstruments: (schoolSlug: string) =>
    apiClient
      .get<ApiResponse<InstrumentOption[]>>(
        `/public/schools/${schoolSlug}/instruments`
      )
      .then((res) => res.data),

  /**
   * Create a new meet & greet booking
   */
  create: (data: MeetAndGreetFormData) =>
    apiClient
      // #412: verificationEmailSent false = the spam check could not run, no email went.
      .post<ApiResponse<{ id: string; message: string; verificationEmailSent: boolean }>>(
        '/public/meet-and-greet',
        data
      )
      .then((res) => res.data),

  /**
   * Verify email address
   */
  verify: (token: string) =>
    apiClient
      .get<
        ApiResponse<{
          message: string;
          meetAndGreet: {
            id: string;
            parentName: string;
            childName: string;
            status: MeetAndGreetStatus;
          };
        }>
      >(`/public/meet-and-greet/verify/${token}`)
      .then((res) => res.data),

  /**
   * Get available teachers and time slots for meet & greet booking
   */
  getAvailability: (schoolSlug: string) =>
    apiClient
      .get<
        ApiResponse<{
          settings: {
            sessionDuration: number;
            bufferTime: number;
            advanceBookingDays: number;
            minimumNoticeHours: number;
          };
          timeSlots: Array<{
            id: string;
            dayOfWeek: number;
            startTime: string;
            endTime: string;
            isActive: boolean;
          }>;
          teachers: Array<{ id: string; name: string }>;
          bookedSlots: Array<{ dateTime: string | null; teacherId: string | null }>;
          unavailablePeriods?: Array<{ teacherId: string; startDate: string; endDate: string }>;
        }>
      >(`/public/schools/${schoolSlug}/meet-and-greet/availability`)
      .then((res) => res.data),
};

// ===========================================
// ADMIN API (Auth Required)
// ===========================================

export interface MeetAndGreetFilters {
  status?: MeetAndGreetStatus;
  startDate?: string;
  endDate?: string;
  teacherId?: string;
}

export interface CreateMeetAndGreetFromEnquiryData {
  enquiryId: string;
  studentFirstName: string;
  studentLastName: string;
  studentAge: number;
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  instrumentId?: string;
  additionalNotes?: string;
  preferredDateTime?: string;
}

export interface AdminCreateMeetAndGreetInput {
  studentFirstName: string;
  studentLastName: string;
  studentAge: number;
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2Name?: string | null;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  instrumentId?: string | null;
  additionalNotes?: string | null;
  scheduledDateTime?: string | null; // ISO 8601; optional at every stage (#425)
  assignedTeacherId?: string | null;
  /** How far the booking goes (#425); the server defaults to FORM. */
  stage?: AdminCreateStage;
  /** Only with stage FORM (the server 400s otherwise). */
  sendVerificationEmail?: boolean;
  /** Link the new booking to this enquiry (needs `enquiries.write` too; 409 `ENQUIRY_ALREADY_LINKED`). */
  enquiryId?: string;
}

/** #425: Initial form → PENDING_VERIFICATION; Verified → PENDING_APPROVAL; Approved → APPROVED + the registration link sent. */
export type AdminCreateStage = 'FORM' | 'VERIFIED' | 'APPROVED';

/** The created row plus what the create sent (#425). */
export type AdminCreateMeetAndGreetResponse = MeetAndGreet & {
  verificationSent?: boolean;
  registrationLinkSent?: boolean;
  /** Minting the link failed; the booking is waiting for approval and Approve sends it. */
  registrationLinkSendFailed?: boolean;
  recipients?: string[];
  emailFailures?: string[];
};

/** Partial patch of the parent-entered fields — every key optional. */
export interface UpdateMeetAndGreetDetailsInput {
  studentFirstName?: string;
  studentLastName?: string;
  contact1Name?: string;
  contact1Email?: string;
  contact1Phone?: string;
  contact1Relationship?: string;
  contact2Name?: string | null;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
}

export type UpdateMeetAndGreetDetailsResponse = MeetAndGreet & {
  /** True when a live registration link was revoked because the email changed. */
  registrationLinkRevoked: boolean;
  emailChanged: boolean;
  /** #249: a new registration link was minted and emailed (see `emailFailures`). */
  registrationLinkSent: boolean;
  /** The re-issue failed before anything was sent; Resend recovers. */
  registrationLinkSendFailed: boolean;
  /** #430: the corrected email belongs to an existing family, so no registration link was sent. */
  registrationLinkSkippedExistingFamily?: boolean;
  /** A new verification email was sent to the corrected address. */
  verificationSent: boolean;
  verificationSendFailed: boolean;
  /** Addresses this save's email(s) were handed to. */
  recipients: string[];
  /** Addresses whose email failed. */
  emailFailures: string[];
};

/** The PATCH body: deliberately has NO `status` (the type refuses what the server answers 400). */
export interface UpdateMeetAndGreetInput {
  assignedTeacherId?: string | null;
  scheduledDateTime?: string | null;
  followUpNotes?: string | null;
}

export const meetAndGreetAdminApi = {
  createFromEnquiry: (data: CreateMeetAndGreetFromEnquiryData) =>
    apiClient
      .post<ApiResponse<{ id: string; message: string }>>('/admin/meet-and-greet/from-enquiry', data)
      .then((res) => res.data),

  adminCreate: (data: AdminCreateMeetAndGreetInput) =>
    apiClient
      .post<ApiResponse<AdminCreateMeetAndGreetResponse>>('/admin/meet-and-greet/admin/create', data)
      .then((res) => res.data),
};

export const meetAndGreetApi = {
  /**
   * Get availability for admin scheduling (with option to exclude a booking)
   */
  getAdminAvailability: (excludeId?: string) =>
    apiClient
      .get<ApiResponse<MeetAndGreetAvailabilityResponse>>(
        '/admin/meet-and-greet/availability',
        { params: excludeId ? { excludeId } : undefined }
      )
      .then((res) => res.data),

  /**
   * Get all meet & greets for school
   */
  getAll: (filters?: MeetAndGreetFilters) =>
    apiClient
      .get<ApiResponse<MeetAndGreet[]>>('/admin/meet-and-greet', {
        params: filters,
      })
      .then((res) => res.data),

  /**
   * Get meet & greet counts by status
   */
  getCounts: () =>
    apiClient
      .get<ApiResponse<MeetAndGreetCounts>>('/admin/meet-and-greet/counts')
      .then((res) => res.data),

  /**
   * Get single meet & greet by ID
   */
  getById: (id: string) =>
    apiClient
      .get<ApiResponse<MeetAndGreet>>(`/admin/meet-and-greet/${id}`)
      .then((res) => res.data),

  /**
   * Update a meet & greet — teacher / schedule / notes ONLY. A `status` key is a 400 since
   * enquiries Phase 3 (a status moves through its own route: `markVerified`, `approve`, ...).
   * Contact fields go through `updateDetails`. 400 `MNG_CONVERTED` / `MNG_FINISHED`.
   */
  update: (id: string, data: UpdateMeetAndGreetInput) =>
    apiClient
      .patch<ApiResponse<MeetAndGreet>>(`/admin/meet-and-greet/${id}`, data)
      .then((res) => res.data),

  /** Staff mark an unverified booking as verified (#304). No body; 400 if no longer awaiting verification. */
  markVerified: (id: string) =>
    apiClient
      .post<ApiResponse<MeetAndGreet>>(`/admin/meet-and-greet/${id}/mark-verified`)
      .then((res) => res.data),

  /**
   * ADMIN: edit the parent-entered details (contacts, emergency, student name).
   * Partial patch. A changed primary email revokes any live registration link
   * and, in the same save (#249), sends a fresh one when the booking may be
   * re-sent one (`canResendRegistration`), or a fresh verification email for a
   * booking awaiting verification. The response reports what was sent.
   */
  updateDetails: (id: string, data: UpdateMeetAndGreetDetailsInput) =>
    apiClient
      .patch<ApiResponse<UpdateMeetAndGreetDetailsResponse>>(`/admin/meet-and-greet/${id}/details`, data)
      .then((res) => res.data),

  /**
   * Approve a meet & greet and send registration link
   */
  approve: (id: string) =>
    apiClient
      .post<
        ApiResponse<{
          message: string;
          registrationUrl: string;
          recipients: string[];
          emailFailures: string[];
        }>
      >(`/admin/meet-and-greet/${id}/approve`)
      .then((res) => res.data),

  /**
   * Re-issue and re-send the registration link for an APPROVED booking.
   * Regenerates the token (the previous link stops working) and restarts the
   * 7-day expiry clock.
   *
   * `registrationUrl` is returned ONLY when every send failed, so the admin has
   * a manual delivery path for a link that is now live but undelivered.
   */
  resendRegistration: (id: string) =>
    apiClient
      .post<
        ApiResponse<{
          message: string;
          recipients: string[];
          expiryDate: string;
          emailFailures: string[];
          registrationUrl?: string;
        }>
      >(`/admin/meet-and-greet/${id}/resend-registration`)
      .then((res) => res.data),

  /**
   * Re-issue and re-send the email-verification link for a booking still
   * awaiting verification. Regenerates the token, invalidating the previous link.
   */
  resendVerification: (id: string) =>
    apiClient
      .post<
        ApiResponse<{
          message: string;
          recipients: string[];
          emailFailures: string[];
        }>
      >(`/admin/meet-and-greet/${id}/resend-verification`)
      .then((res) => res.data),

  /**
   * Reject a meet & greet with reason
   */
  reject: (id: string, reason: string) =>
    apiClient
      .post<ApiResponse<MeetAndGreet>>(`/admin/meet-and-greet/${id}/reject`, {
        reason,
      })
      .then((res) => res.data),

  /**
   * Place a meet & greet on the waitlist
   */
  waitlist: (id: string, reason?: string, notifyParent: boolean = true) =>
    apiClient
      .post<ApiResponse<MeetAndGreet>>(`/admin/meet-and-greet/${id}/waitlist`, {
        reason,
        notifyParent,
      })
      .then((res) => res.data),

  /** Owner only: move the review day of a waitlisted booking. `reviewOn` is a school-local YYYY-MM-DD after today. */
  extendWaitlist: (id: string, body: { reviewOn: string; note?: string }) =>
    apiClient
      .post<ApiResponse<{ waitlistReviewAt: string }>>(`/admin/meet-and-greet/${id}/waitlist/extend`, body)
      .then((res) => res.data),

  /** Owner only: the waitlist lapsed. Rejects the booking, closes its enquiry, keeps a held fee. Emails nobody. */
  confirmWaitlistLapsed: (id: string) =>
    apiClient
      .post<ApiResponse<WaitlistConfirmResult>>(`/admin/meet-and-greet/${id}/waitlist/confirm`, {})
      .then((res) => res.data),

  /**
   * Cancel a meet & greet
   */
  cancel: (id: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(`/admin/meet-and-greet/${id}`)
      .then((res) => res.data),

  /**
   * Admin convert: creates family/student accounts and records the
   * registration-fee outcome the admin chose (paid online, paid another way,
   * not collected yet, or waived) in the same transaction.
   */
  adminConvert: (id: string, registrationFee: AdminConvertRegistrationFee, confirmNewFamily?: boolean) =>
    apiClient
      .post<
        ApiResponse<{
          message: string;
          familyId: string;
          parentUserId: string;
          studentId: string;
          registrationFee: { outcome: AdminConvertRegistrationFee['mode']; registrationPaymentId: string | null };
        }>
      >(`/admin/meet-and-greet/${id}/convert`, {
        registrationFee,
        // #430 D14: the office's logged "this is a different family" tick.
        ...(confirmNewFamily ? { confirmNewFamily: true } : {}),
      })
      .then((res) => res.data),

  /**
   * What the Convert dialog needs to offer the registration-fee choice: the
   * earliest COMPLETED online (Stripe) payment for this Meet & Greet, if any,
   * and the school's default fee to prefill "Paid another way".
   */
  getRegistrationFee: (id: string) =>
    apiClient
      .get<
        ApiResponse<{
          onlinePayment: { id: string; amount: string; paidAt: string | null; currency: string } | null;
          defaultAmount: string;
        }>
      >(`/admin/meet-and-greet/${id}/registration-fee`)
      .then((res) => res.data),

  // ===========================================
  // SETTINGS
  // ===========================================

  /**
   * Get meet & greet settings
   */
  getSettings: () =>
    apiClient
      .get<ApiResponse<MeetAndGreetSettingsResponse>>('/admin/meet-and-greet/settings')
      .then((res) => res.data),

  /**
   * Update meet & greet settings
   */
  updateSettings: (data: {
    sessionDuration?: number;
    bufferTime?: number;
    advanceBookingDays?: number;
    minimumNoticeHours?: number;
  }) =>
    apiClient
      .patch<ApiResponse<MeetAndGreetSettingsResponse>>('/admin/meet-and-greet/settings', data)
      .then((res) => res.data),

  /**
   * Add a time slot
   */
  addTimeSlot: (data: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive?: boolean;
  }) =>
    apiClient
      .post<ApiResponse<MeetAndGreetSettingsResponse>>(
        '/admin/meet-and-greet/settings/time-slots',
        data
      )
      .then((res) => res.data),

  /**
   * Bulk set time slots (replaces existing)
   */
  setTimeSlots: (slots: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive?: boolean;
  }>) =>
    apiClient
      .put<ApiResponse<MeetAndGreetSettingsResponse>>(
        '/admin/meet-and-greet/settings/time-slots',
        { slots }
      )
      .then((res) => res.data),

  /**
   * Update a time slot
   */
  updateTimeSlot: (slotId: string, data: {
    dayOfWeek?: number;
    startTime?: string;
    endTime?: string;
    isActive?: boolean;
  }) =>
    apiClient
      .patch<ApiResponse<MeetAndGreetSettingsResponse>>(
        `/admin/meet-and-greet/settings/time-slots/${slotId}`,
        data
      )
      .then((res) => res.data),

  /**
   * Delete a time slot
   */
  deleteTimeSlot: (slotId: string) =>
    apiClient
      .delete<ApiResponse<MeetAndGreetSettingsResponse>>(
        `/admin/meet-and-greet/settings/time-slots/${slotId}`
      )
      .then((res) => res.data),
};
